import { useState, useId } from 'react';
import { ChevronDown, ChevronUp, MapPin, Plus, Trash2 } from 'lucide-react';
import {
  ADDRESS_LABEL_PRESETS,
  formatAddressSummary,
  getCountryNameOptions,
  type CustomerAddress,
} from '@shared/components';
import GoogleMapPicker from '../delivery/GoogleMapPicker';

/** Sentinel for the "Other…" choice in the label dropdown. */
const OTHER = '__other__';

export interface AddressBookProps {
  addresses: CustomerAddress[];
  darkMode?: boolean;
  onAddressChange: (index: number, field: keyof CustomerAddress, value: unknown) => void;
  onAddAddress: () => void;
  onRemoveAddress: (index: number) => void;
  onSetPrimaryAddress: (index: number) => void;
}

// Controls are labelled natively (htmlFor/id) because a page can hold several
// address cards with identically-named fields.
const fieldClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm ' +
  'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ' +
  'focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent';
const labelClass = 'block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1';

const TextField = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div>
    <label className={labelClass} htmlFor={id}>
      {label}
    </label>
    <input
      id={id}
      type="text"
      className={fieldClass}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

interface AddressCardProps {
  address: CustomerAddress;
  index: number;
  canRemove: boolean;
  defaultExpanded: boolean;
  onAddressChange: AddressBookProps['onAddressChange'];
  onRemoveAddress: AddressBookProps['onRemoveAddress'];
  onSetPrimaryAddress: AddressBookProps['onSetPrimaryAddress'];
}

const AddressCard = ({
  address,
  index,
  canRemove,
  defaultExpanded,
  onAddressChange,
  onRemoveAddress,
  onSetPrimaryAddress,
}: AddressCardProps) => {
  const uid = useId();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const label = address.name || '';
  const isPreset = ADDRESS_LABEL_PRESETS.indexOf(label) !== -1;
  const [isCustomLabel, setIsCustomLabel] = useState(Boolean(label) && !isPreset);
  const showCustom = isCustomLabel || (Boolean(label) && !isPreset);

  const set = (field: keyof CustomerAddress) => (value: unknown) =>
    onAddressChange(index, field, value);

  const summary = formatAddressSummary(address);

  return (
    <div
      data-testid="address-card"
      className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
    >
      <button
        type="button"
        data-testid="address-card-toggle"
        onClick={() => setIsExpanded((open) => !open)}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0">
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`h-2 w-2 rounded-full ${
                address.isPrimary
                  ? 'bg-[var(--primary-color)]'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {label || 'Untitled address'}
            </span>
            {address.isPrimary && (
              <span
                data-testid="primary-badge"
                className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              >
                Primary
              </span>
            )}
          </span>
          {summary && (
            <span className="block truncate text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {summary}
            </span>
          )}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor={`${uid}-label`}>
                Label
              </label>
              <select
                id={`${uid}-label`}
                className={fieldClass}
                value={showCustom ? OTHER : label}
                onChange={(e) => {
                  if (e.target.value === OTHER) {
                    setIsCustomLabel(true);
                    set('name')('');
                  } else {
                    setIsCustomLabel(false);
                    set('name')(e.target.value);
                  }
                }}
              >
                {ADDRESS_LABEL_PRESETS.map((preset: string) => (
                  <option key={preset} value={preset}>
                    {preset}
                  </option>
                ))}
                <option value={OTHER}>Other…</option>
              </select>
            </div>
            {showCustom && (
              <TextField
                id={`${uid}-custom-label`}
                label="Custom label"
                value={label}
                onChange={set('name')}
              />
            )}
          </div>

          <TextField
            id={`${uid}-street1`}
            label="Street Address 1"
            value={address.street1 || ''}
            onChange={set('street1')}
          />
          <TextField
            id={`${uid}-street2`}
            label="Street Address 2"
            value={address.street2 || ''}
            onChange={set('street2')}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id={`${uid}-city`}
              label="City"
              value={address.city || ''}
              onChange={set('city')}
            />
            <TextField
              id={`${uid}-state`}
              label="State/Parish"
              value={address.state || ''}
              onChange={set('state')}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              id={`${uid}-zip`}
              label="ZIP/Postal Code"
              value={address.zip || ''}
              onChange={set('zip')}
            />
            <div>
              <label className={labelClass} htmlFor={`${uid}-country`}>
                Country
              </label>
              <select
                id={`${uid}-country`}
                className={fieldClass}
                value={address.country || ''}
                onChange={(e) => set('country')(e.target.value)}
              >
                <option value="">Select a country</option>
                {getCountryNameOptions().map((option: { value: string; label: string }) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setIsMapOpen((open) => !open)}
              aria-expanded={isMapOpen}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--primary-color)] hover:underline"
            >
              <MapPin className="w-4 h-4" />
              Pin location
              {address.coordinates && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({address.coordinates.lat.toFixed(4)}, {address.coordinates.lng.toFixed(4)})
                </span>
              )}
            </button>
            {isMapOpen && (
              <div className="mt-3">
                <GoogleMapPicker
                  value={{
                    address: formatAddressSummary(address),
                    coordinates: address.coordinates || { lat: 0, lng: 0 },
                  }}
                  onChange={(location) => set('coordinates')(location.coordinates)}
                  height="240px"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
            {address.isPrimary ? (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Your main address for deliveries and invoices.
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onSetPrimaryAddress(index)}
                className="text-sm font-medium text-[var(--primary-color)] hover:underline"
              >
                Make primary
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                onClick={() => onRemoveAddress(index)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * The customer's own address book. Exactly one entry is primary, and that is
 * the one the courier uses for deliveries, invoices and exports.
 */
const AddressBook = ({
  addresses,
  onAddressChange,
  onAddAddress,
  onRemoveAddress,
  onSetPrimaryAddress,
}: AddressBookProps) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Addresses</h3>
      <button
        type="button"
        onClick={onAddAddress}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--primary-color)] border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <Plus className="w-4 h-4" />
        Add address
      </button>
    </div>
    <div className="space-y-3">
      {addresses.map((address, index) => (
        <AddressCard
          key={address.id ?? index}
          address={address}
          index={index}
          canRemove={addresses.length > 1}
          defaultExpanded={addresses.length === 1}
          onAddressChange={onAddressChange}
          onRemoveAddress={onRemoveAddress}
          onSetPrimaryAddress={onSetPrimaryAddress}
        />
      ))}
    </div>
  </div>
);

export default AddressBook;
